import { useLoggedInMember } from "@/modules/auth/states";
import { getZaniaTeamMemberRoles } from "@/modules/team/states";
import { LoadingSpinner } from "@/shared/components/custom/spin";
import { ROUTES } from "@/shared/constants/routes";
import { addNotification } from "@/shared/states/notification";
import { getApiErrorMessage, isValidationError } from "@/shared/utils/api-error-handler";
import parsePromise from "@/shared/utils/parsePromise";
import { Chip, Switch } from "@nextui-org/react";
import { ArrowLeft, Check, Edit, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getContinuousMonitoringConfigurations,
  updateContinuousMonitoringConfiguration,
  type ContinuousMonitoringConfiguration
} from "../../requests/continuous-monitoring";
import { useSettingsHeaderStore } from "../../states/settings-header";
import ExitWithoutSavingModal from "./ExitWithoutSavingModal";

interface MonitoringRule {
  id: string;
  title: string;
  enabled: boolean;
  description: string;
  when: {
    title: string;
    description: string;
  };
  then: {
    notify: boolean;
    recipients: string[];
    reassess: boolean;
  };
}

const ContinuousMonitoring = () => {
  const navigate = useNavigate();
  const member = useLoggedInMember();
  const { isTeamOrgAdmin, isTeamAdmin } = member
    ? getZaniaTeamMemberRoles("", member)
    : { isTeamOrgAdmin: false, isTeamAdmin: false };

  const isAdmin = isTeamOrgAdmin || isTeamAdmin;

  // Settings header store for Done button state
  const { setIsSaving: setHeaderIsSaving, setIsDisabled: setHeaderIsDisabled } = useSettingsHeaderStore();

  // State for email input and validation
  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");

  // Modal state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isExitModalOpen, setIsExitModalOpen] = useState(false);

  // State to track which rule is being edited
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);

  // Track if user has interacted with email input (to show validation errors only after interaction)
  const [hasInteractedWithEmail, setHasInteractedWithEmail] = useState(false);

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Original configurations from API (for cancel functionality)
  const [originalConfigurations, setOriginalConfigurations] = useState<ContinuousMonitoringConfiguration[]>([]);

  // Email validation constants
  const MAX_EMAIL_LENGTH = 40;

  // Email validation function
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  // Check if email exceeds max length
  const isEmailTooLong = (email: string): boolean => {
    return email.trim().length > MAX_EMAIL_LENGTH;
  };

  // Check if recipients array is empty AND no valid email in input
  const hasRecipientsError = (recipients: string[], currentInput: string = ""): boolean => {
    // If there are already recipients, it's valid
    if (recipients.length > 0) return false;

    // If there's no input, it's an error
    if (!currentInput.trim()) return true;

    // Check if the current input is a valid email (even without comma)
    return !validateEmail(currentInput);
  };

  // Helper function to map API configuration to MonitoringRule
  const mapConfigToRule = (config: ContinuousMonitoringConfiguration): MonitoringRule => {
    // Format rules array into description text with bullet points
    const rulesDescription = config.rules.length > 0
      ? config.rules.map(rule => `• ${rule}`).join('\n')
      : "No rules configured.";

    return {
      id: config.id,
      title: config.title,
      enabled: config.enabled,
      description: `Automatically sends alerts and triggers actions based on configured rules.`,
      when: {
        title: "When",
        description: rulesDescription
      },
      then: {
        notify: config.actions.includes("notify_recipients"),
        recipients: config.email_recipients,
        reassess: false // Not supported in current API
      }
    };
  };

  // Helper function to map MonitoringRule to API update payload
  const mapRuleToUpdatePayload = (rule: MonitoringRule, organizationId: string) => {
    const actions: ("notify_recipients")[] = [];

    if (rule.then.notify) {
      actions.push("notify_recipients");
    }

    return {
      enabled: rule.enabled,
      organization_id: organizationId,
      email_recipients: rule.then.notify ? rule.then.recipients : [], // Empty array if notify is false
      actions
    };
  };

  // Mock data - replace with actual API data
  const [rules, setRules] = useState<MonitoringRule[]>([]);

  // Use refs to avoid stale closures in event listeners
  const emailInputRef = useRef(emailInput);
  const editingRuleIdRef = useRef(editingRuleId);

  useEffect(() => {
    emailInputRef.current = emailInput;
  }, [emailInput]);

  useEffect(() => {
    editingRuleIdRef.current = editingRuleId;
  }, [editingRuleId]);

  // Fetch configurations on component mount
  useEffect(() => {
    const fetchConfigurations = async () => {
      setIsLoading(true);
      const [error, response] = await parsePromise(getContinuousMonitoringConfigurations());

      if (error) {
        addNotification({
          type: "error",
          title: "Failed to load configurations",
          message: error.message || "Could not load continuous monitoring configurations"
        });
        setIsLoading(false);
        return;
      }

      if (response && response.configurations) {
        // Sort configurations by index in ascending order
        const sortedConfigurations = [...response.configurations].sort((a, b) => a.index - b.index);
        setOriginalConfigurations(sortedConfigurations);
        const mappedRules = sortedConfigurations.map(mapConfigToRule);
        setRules(mappedRules);
      }

      setIsLoading(false);
    };

    void fetchConfigurations();
  }, []);

  // Clean up header store on unmount
  useEffect(() => {
    return () => {
      setHeaderIsSaving(false);
      setHeaderIsDisabled(false);
    };
  }, [setHeaderIsSaving, setHeaderIsDisabled]);

  // Update header disabled state when editing and rules change
  useEffect(() => {
    // Disable Done button if any card is open (being edited)
    setHeaderIsDisabled(editingRuleId !== null);
  }, [editingRuleId, setHeaderIsDisabled]);

  // Listen for Done button click from layout header
  useEffect(() => {
    const handleDoneClick = () => {
      // Check for unsaved changes before navigating
      if (hasUnsavedChanges) {
        setIsExitModalOpen(true);
      } else {
        navigate(`/${ROUTES.SETTINGS}?tab=third-party`, { replace: true });
      }
    };

    window.addEventListener('continuousMonitoringDone', handleDoneClick);
    return () => {
      window.removeEventListener('continuousMonitoringDone', handleDoneClick);
    };
  }, [navigate, hasUnsavedChanges]);

  const handleEmailInputChange = (ruleId: string, value: string) => {
    setEmailInput(value);
    setEmailError("");
    // Don't set hasInteractedWithEmail here - only set it after blur or save attempt

    // Check if user typed a comma or space
    if (value.includes(',') || value.includes(' ')) {
      // Split by both comma and space, then filter empty strings, and convert to lowercase
      const emails = value.split(/[,\s]+/).map(e => e.trim().toLowerCase()).filter(e => e);
      const lastEmail = emails[emails.length - 1];

      // Process all complete emails (before the last comma/space)
      const completeEmails = (value.endsWith(',') || value.endsWith(' ')) ? emails : emails.slice(0, -1);

      if (completeEmails.length > 0) {
        setRules(prevRules => {
          const rule = prevRules.find(r => r.id === ruleId);
          const invalidEmails: string[] = [];
          const duplicateEmails: string[] = [];
          const validEmails: string[] = [];

          const tooLongEmails: string[] = [];

          completeEmails.forEach(email => {
            if (isEmailTooLong(email)) {
              tooLongEmails.push(email);
            } else if (!validateEmail(email)) {
              invalidEmails.push(email);
            } else if (rule?.then.recipients.includes(email)) {
              duplicateEmails.push(email);
            } else if (!validEmails.includes(email)) {
              validEmails.push(email);
            }
          });

          if (tooLongEmails.length > 0) {
            setEmailError(`Email must be ${MAX_EMAIL_LENGTH} characters or less`);
            return prevRules;
          }

          if (invalidEmails.length > 0) {
            setEmailError(`Invalid email: ${invalidEmails[0]}`);
            return prevRules;
          }

          if (duplicateEmails.length > 0) {
            setEmailError(`Email already added: ${duplicateEmails[0]}`);
          }

          if (validEmails.length > 0) {
            setHasUnsavedChanges(true);
            return prevRules.map(rule =>
              rule.id === ruleId
                ? {
                  ...rule,
                  then: {
                    ...rule.then,
                    recipients: [...rule.then.recipients, ...validEmails]
                  }
                }
                : rule
            );
          }

          return prevRules;
        });

        // Clear input if it ends with comma or space, otherwise keep the last email
        setEmailInput((value.endsWith(',') || value.endsWith(' ')) ? '' : lastEmail);
      }
    }
  };

  const removeRecipient = (ruleId: string, email: string) => {
    setRules(prevRules => prevRules.map(rule =>
      rule.id === ruleId
        ? {
          ...rule,
          then: {
            ...rule.then,
            recipients: rule.then.recipients.filter(r => r !== email)
          }
        }
        : rule
    ));
    setEmailInput("");
    setEmailError("");
    setHasInteractedWithEmail(false);
    setHasUnsavedChanges(true);
  };

  const toggleNotify = (ruleId: string) => {
    setRules(prevRules => prevRules.map(rule => {
      if (rule.id === ruleId) {
        const newNotifyValue = !rule.then.notify;
        // When turning on notify, enable the rule. When turning off notify, disable the rule
        const newEnabledValue = newNotifyValue;
        return {
          ...rule,
          enabled: newEnabledValue,
          then: {
            ...rule.then,
            notify: newNotifyValue,
            // Clear recipients when turning off notify
            recipients: newNotifyValue ? rule.then.recipients : []
          }
        };
      }
      return rule;
    }));
    // Clear email input if we're editing this rule and turning off notify
    if (editingRuleId === ruleId) {
      setEmailInput("");
      setEmailError("");
      setHasInteractedWithEmail(false);
    }
    setHasUnsavedChanges(true);
  };

  const handleEditRule = (ruleId: string) => {
    setEditingRuleId(ruleId);
  };

  const handleCancelEdit = (ruleId: string) => {
    // Restore from original configuration
    const originalConfig = originalConfigurations.find(c => c.id === ruleId);
    if (originalConfig) {
      const restoredRule = mapConfigToRule(originalConfig);
      setRules(prevRules => prevRules.map(rule => rule.id === ruleId ? restoredRule : rule));
    }

    setEditingRuleId(null);
    setEmailInput("");
    setEmailError("");
    setHasInteractedWithEmail(false);
    setHasUnsavedChanges(false);
  };

  const handleSaveEdit = async (ruleId: string) => {
    // Validate that recipients are not empty if notify is enabled
    const rule = rules.find(r => r.id === ruleId);
    if (!rule) return;

    if (rule.then.notify && hasRecipientsError(rule.then.recipients, emailInput)) {
      // Ensure user sees validation UI when save is attempted
      setHasInteractedWithEmail(true);

      // Provide helpful inline error
      if (rule.then.recipients.length === 0 && !emailInput.trim()) {
        setEmailError("Please add at least one recipient");
      } else if (emailInput.trim() && !validateEmail(emailInput.trim())) {
        setEmailError(`Invalid email: ${emailInput.trim()}`);
      }

      return; // Don't save if validation fails
    }

    // If there's a valid email in the input (not yet added as a chip), add it before saving
    let ruleToSave = rule;
    const trimmedEmail = emailInput.trim().toLowerCase();
    if (trimmedEmail && validateEmail(trimmedEmail)) {
      // Check for length before adding
      if (isEmailTooLong(trimmedEmail)) {
        setEmailError(`Email must be ${MAX_EMAIL_LENGTH} characters or less`);
        return;
      }
      // Check for duplicate before adding
      if (rule.then.recipients.includes(trimmedEmail)) {
        setEmailError(`Email already added: ${trimmedEmail}`);
        return;
      }
      ruleToSave = {
        ...rule,
        then: {
          ...rule.then,
          recipients: [...rule.then.recipients, trimmedEmail]
        }
      };
      setRules(prevRules => prevRules.map(r =>
        r.id === ruleId ? ruleToSave : r
      ));
      // Clear the input immediately after adding the email
      setEmailInput("");
    }

    // Save to API
    setIsSaving(true);
    const payload = mapRuleToUpdatePayload(ruleToSave, member?.organization_id || '');
    const [error] = await parsePromise(
      updateContinuousMonitoringConfiguration(ruleId, payload)
    );

    setIsSaving(false);

    if (error) {
      const axiosError = error as any; // Cast to access AxiosError properties

      // For validation errors, show in UI instead of toast
      if (isValidationError(axiosError)) {
        setEmailError(axiosError.response?.data?.error_message || axiosError.message);
        setHasInteractedWithEmail(true);
        return;
      }

      // For other errors, show toast
      addNotification({
        type: "error",
        title: "Failed to save configuration",
        message: getApiErrorMessage(axiosError)
      });
      return;
    }

    addNotification({
      type: "success",
      title: "Success",
      message: "Continuous monitoring configuration saved successfully"
    });

    // Update original configurations with the saved rule
    setOriginalConfigurations(prevConfigs =>
      prevConfigs.map(config => {
        if (config.id === ruleId) {
          return {
            ...config,
            enabled: ruleToSave.enabled,
            email_recipients: ruleToSave.then.recipients,
            actions: ruleToSave.then.notify ? ["notify_recipients" as const] : []
          };
        }
        return config;
      })
    );

    setEditingRuleId(null);
    setEmailError("");
    setHasInteractedWithEmail(false);
    setHasUnsavedChanges(false);
  };

  const handleBackClick = () => {
    if (hasUnsavedChanges) {
      setIsExitModalOpen(true);
    } else {
      navigate(`/${ROUTES.SETTINGS}?tab=third-party`, { replace: true });
    }
  };

  const handleExitWithoutSaving = () => {
    setHasUnsavedChanges(false);
    navigate(`/${ROUTES.SETTINGS}?tab=third-party`, { replace: true });
  };

  const handleSaveAndExit = async () => {
    // Find the currently edited rule
    const editedRule = rules.find(r => r.id === editingRuleId);
    if (!editedRule || !editingRuleId) {
      // If no rule is being edited, just navigate
      navigate(`/${ROUTES.SETTINGS}?tab=third-party`, { replace: true });
      return;
    }

    // Validate before saving
    if (editedRule.then.notify && hasRecipientsError(editedRule.then.recipients, emailInput)) {
      setHasInteractedWithEmail(true);
      setIsExitModalOpen(false);
      return;
    }

    // If there's a valid email in the input (not yet added as a chip), add it before saving
    let ruleToSave = editedRule;
    const trimmedEmail = emailInput.trim().toLowerCase();
    if (trimmedEmail && validateEmail(trimmedEmail)) {
      // Check for length before adding
      if (isEmailTooLong(trimmedEmail)) {
        setEmailError(`Email must be ${MAX_EMAIL_LENGTH} characters or less`);
        setIsExitModalOpen(false);
        return;
      }
      // Check for duplicate before adding
      if (editedRule.then.recipients.includes(trimmedEmail)) {
        setEmailError(`Email already added: ${trimmedEmail}`);
        setIsExitModalOpen(false);
        return;
      }
      ruleToSave = {
        ...editedRule,
        then: {
          ...editedRule.then,
          recipients: [...editedRule.then.recipients, trimmedEmail]
        }
      };
      setRules(prevRules => prevRules.map(r =>
        r.id === editingRuleId ? ruleToSave : r
      ));
      // Clear the input immediately after adding the email
      setEmailInput("");
    }

    // Save to API
    setIsSaving(true);
    const payload = mapRuleToUpdatePayload(ruleToSave, member?.organization_id || '');
    const [error] = await parsePromise(
      updateContinuousMonitoringConfiguration(editingRuleId, payload)
    );

    setIsSaving(false);

    if (error) {
      const axiosError = error as any;

      // For validation errors, show in UI and close modal
      if (isValidationError(axiosError)) {
        setEmailError(axiosError.response?.data?.error_message || axiosError.message);
        setHasInteractedWithEmail(true);
        setIsExitModalOpen(false);
        return;
      }

      // For other errors, show toast and close modal
      addNotification({
        type: "error",
        title: "Failed to save configuration",
        message: getApiErrorMessage(axiosError)
      });
      setIsExitModalOpen(false);
      return;
    }

    // Success - show notification, close modal and navigate
    addNotification({
      type: "success",
      title: "Success",
      message: "Continuous monitoring configuration saved successfully"
    });

    setEditingRuleId(null);
    setEmailError("");
    setHasInteractedWithEmail(false);
    setIsExitModalOpen(false);
    setHasUnsavedChanges(false);
    navigate(`/${ROUTES.SETTINGS}?tab=third-party`, { replace: true });
  };

  return (
    <div className="w-full h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-4 bg-white">
        <button
          onClick={handleBackClick}
          className="flex items-center justify-center w-5 h-5 hover:bg-gray-100 rounded"
        >
          <ArrowLeft className="w-5 h-5 text-black" />
        </button>
        <h1 className="text-sm font-medium text-[#171717] flex-1">
          Continuous Monitoring
        </h1>
      </div>

      {/* Content */}
      <div className="flex-1 p-4 bg-[#FEFEFE]">

        {/* Loading State */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner
              containerClassName="bg-transparent relative"
              className="mx-auto"
            />
          </div>
        ) : (
          <>
            {/* Content Area */}
            <div className="flex flex-col items-center gap-4">
              <div className="flex flex-row gap-4 w-full max-w-[960px]">
                {rules.map((rule, index) => (
                  <div
                    key={rule.id}
                    className="flex flex-col gap-4 flex-1"
                  >
                    {/* Rule Card */}
                    <div
                      className="flex flex-col p-4 gap-4 bg-white rounded-lg"
                      style={{
                        border: editingRuleId === rule.id ? '1px solid #A1A1AA' : '1px solid #E4E4E7',
                        boxShadow: editingRuleId === rule.id ? '0px 4px 8px rgba(0, 0, 0, 0.05)' : 'none'
                      }}
                    >
                      {/* Title Row */}
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-1.5">
                          <h5 className="text-sm font-medium text-[#18181B] flex-1 truncate">
                            {rule.title}
                          </h5>
                          <Chip
                            size="sm"
                            classNames={{
                              base: "h-4 px-1.5",
                              content: "px-0 text-[10px] font-medium uppercase"
                            }}
                            style={{
                              background: rule.enabled ? '#E8FAF0' : '#F4F4F5',
                              color: rule.enabled ? '#0E793C' : '#71717A'
                            }}
                          >
                            {rule.enabled ? 'Enabled' : 'Disabled'}
                          </Chip>
                          {editingRuleId === rule.id ? (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleCancelEdit(rule.id)}
                                disabled={isSaving}
                                className="w-6 h-6 flex items-center justify-center rounded-md bg-[#F4F4F5] disabled:opacity-50"
                              >
                                <X className="w-4 h-4 text-[#71717A]" />
                              </button>
                              <button
                                onClick={() => handleSaveEdit(rule.id)}
                                disabled={isSaving}
                                className="w-6 h-6 flex items-center justify-center rounded-md bg-[#006FEE] disabled:opacity-50"
                              >
                                <Check className="w-4 h-4 text-white" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleEditRule(rule.id)}
                              disabled={isSaving || editingRuleId !== null}
                              className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-[#F4F4F5] disabled:opacity-50"
                            >
                              <Edit className="w-4 h-4 text-[#71717A]" />
                            </button>
                          )}
                        </div>

                        {/* Description */}
                        <p className="text-xs text-[#71717A] leading-4">
                          {rule.description}
                        </p>
                      </div>

                      {/* When Section */}
                      {editingRuleId === rule.id && (
                        <div className="flex flex-col gap-1.5">
                          <p className="text-xs text-[#18181B]">{`When`}</p>
                          <div className="p-3 bg-[#FAFAFA] rounded">
                            <p className="text-sm text-[#3F3F46] leading-5 whitespace-pre-line break-words">
                              {rule.when.description}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Then Section */}
                      {editingRuleId === rule.id && (
                        <div className="flex flex-col gap-2">
                          <p className="text-xs text-[#18181B]">Then</p>

                          <div className="flex flex-col p-3 gap-4 bg-[#FAFAFA] rounded">
                            {/* Notify Option */}
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center justify-between">
                                <p className="text-sm text-[#3F3F46]">Notify through an email</p>
                                <Switch
                                  size="sm"
                                  isSelected={rule.then.notify}
                                  onValueChange={() => toggleNotify(rule.id)}
                                  isDisabled={isSaving}
                                  classNames={{
                                    wrapper: "group-data-[selected=true]:bg-[#006FEE]"
                                  }}
                                />
                              </div>

                              {/* Recipients */}
                              {rule.then.notify && (
                                <div className="flex flex-col gap-1.5">
                                  <p className="text-xs text-[#18181B]">Recipients</p>

                                  {/* Combined input with chips */}
                                  <div
                                    className={`flex flex-wrap items-center gap-1.5 p-2 bg-white border rounded-md min-h-[40px] w-full overflow-hidden ${(hasRecipientsError(rule.then.recipients, emailInput) && hasInteractedWithEmail)
                                      ? "border-[#F31260]"
                                      : emailError
                                        ? "border-[#F31260]"
                                        : "border-[#E4E4E7]"
                                      }`}
                                  >
                                    {rule.then.recipients.map((email) => (
                                      <Chip
                                        key={email}
                                        onClose={() => removeRecipient(rule.id, email)}
                                        variant="flat"
                                        size="sm"
                                        classNames={{
                                          base: "bg-[#F4F4F5] h-6 max-w-full min-w-0",
                                          content: "text-xs text-[#3F3F46] truncate",
                                          closeButton: "text-[#71717A] hover:text-[#18181B] flex-shrink-0"
                                        }}
                                      >
                                        {email}
                                      </Chip>
                                    ))}
                                    <input
                                      type="text"
                                      placeholder={rule.then.recipients.length === 0 ? "Enter email addresses separated by commas" : ""}
                                      value={emailInput}
                                      onChange={(e) => handleEmailInputChange(rule.id, e.target.value)}
                                      onBlur={() => setHasInteractedWithEmail(true)}
                                      disabled={isSaving}
                                      className="flex-1 min-w-[120px] outline-none text-sm bg-transparent disabled:opacity-50"
                                    />
                                  </div>

                                  {/* Validation errors */}
                                  {emailError && (
                                    <p className="text-xs text-[#F31260]">{emailError}</p>
                                  )}
                                  {hasRecipientsError(rule.then.recipients, emailInput) && !emailError && hasInteractedWithEmail && (
                                    <p className="text-xs text-[#F31260]">
                                      Please add at least one recipient
                                    </p>
                                  )}

                                  {/* Organization note */}
                                  {/* {!emailError && !hasRecipientsError(rule.then.recipients, emailInput) && (
                              <p className="text-xs text-[#71717A]">
                                Recipients must be part of your organization.
                              </p>
                            )} */}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

      </div>

      {/* Exit Without Saving Modal */}
      <ExitWithoutSavingModal
        isOpen={isExitModalOpen}
        toggleModal={() => setIsExitModalOpen(false)}
        onExitWithoutSaving={handleExitWithoutSaving}
        onSaveAndExit={handleSaveAndExit}
        isLoading={isSaving}
      />
    </div>
  );
};

export default ContinuousMonitoring;
